import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import styled from '@emotion/styled';
import autobind from 'autobind-decorator';
import { AppUI } from '@seedlang/state';
import { Theme } from '@seedlang/constants';
import Modal from 'components/modal';
import Button from 'components/button/button';
import Spinner from 'components/spinner';
import { Link } from 'react-router';
import { gaps } from 'components/layout/stack';

const DeleteModal = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${gaps.md};
  p {
    margin: 20px 0 0 0;
  }
  padding: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${props => gaps[props.gap]};

  & > * {
    width: 100%;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${Theme.darkestBlue};
  text-decoration: underline;
  cursor: pointer;
  font-size: 16px;
`;

@observer
class DeleteAccountModal extends React.Component {
  @observable showDeletionConfirmation = false;
  @observable deletionSuccess = false;
  @observable showDeletionSpinner = false;

  @autobind onDeleteUser() {
    this.showDeletionSpinner = true;
    AppUI.userStore.destroy({ ids: { userId: AppUI.user.id } }, this.afterDeleteUser);
  }

  @autobind afterDeleteUser(resp) {
    this.showDeletionConfirmation = true;
    this.showDeletionSpinner = false;
    this.deletionSuccess = resp['success'];
    if (this.deletionSuccess) {
      AppUI.signOut();
    }
  }

  @computed get otherSitesMessage() {
    if (!this.props.otherSitesWarning) return;
    return `⚠️ This will also delete your account on ${this.props.otherSitesWarning}.`;
  }

  render() {
    const {
      onClose,
      activeStripeSubscriptionExists,
    } = this.props;

    return (
      <Modal
        onCloseModal={onClose}
        width='100%'
        maxWidth='500px'
        title='Delete Account'
        background={Theme.red}
        contentBackground='#FFF'
      >
        <DeleteModal>
          {
            !this.showDeletionConfirmation && !this.showDeletionSpinner &&
            <>
              {
                activeStripeSubscriptionExists
                  ? 'Deleting your account is irreversible. To keep your progress and free features, you can cancel your membership instead and return anytime.'
                  : 'Permanently delete your user account and all of your data? This cannot be undone.'
              }
              {
                this.otherSitesMessage &&
                  <strong>{this.otherSitesMessage}</strong>
              }
              <ButtonGroup gap='md'>
                <ButtonGroup gap='sm'>
                  {
                    activeStripeSubscriptionExists &&
                    <Link
                      to={{name: 'settings.billing'}}
                    >
                      <Button
                        height='50px'
                        borderRadius='30px'
                        background={Theme.green}
                        width='100%'
                      >
                        Manage Membership
                      </Button>
                    </Link>
                  }
                  <Button
                    onClick={this.onDeleteUser}
                    background={Theme.red}
                    width='100%'
                    height='50px'
                    borderRadius='30px'
                  >
                    Delete Account
                  </Button>
                </ButtonGroup>
                <LinkButton
                  onClick={onClose}
                >
                  Cancel
                </LinkButton>
              </ButtonGroup>
            </>
          }
          {
            this.showDeletionSpinner &&
            <div style={{ textAlign: 'center' }}>
              <div>Your account is being deleted.</div>
              <div>You will be signed out.</div>
              <Spinner background={AppUI.site.accentColor} />
            </div>
          }
          {
            this.showDeletionConfirmation && !this.deletionSuccess &&
            <div style={{ textAlign: 'center' }}>
              <div>Your account will be deleted by our team within 72 hours.</div>
              <br />
              <div>You can contact our support team if you have any questions.</div>
              <Link to='contact'>
                <Button
                  background={AppUI.siteIsDefault ? Theme.blue : AppUI.site.accentColor}
                  margin='10px 0 0 0'
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          }
        </DeleteModal>
      </Modal>
    );
  }
}

export default DeleteAccountModal;